import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { InfoCircle } from 'iconsax-reactjs';
import { fibLevels, timeframeShort, type ResearchNote } from '@pipyar/core';
import { useTheme } from '@/lib/useTheme';
import { useI18n } from '@/lib/i18n';
import { FibChart } from '@/components/chart/FibChart';
import { ChartFrame } from '@/components/chart/ChartFrame';
import { chartPalette } from '@/components/chart/chartTheme';
import type { FibDrawing } from '@/components/chart/fibPrimitive';
import { FibTabs } from './FibTabs';
import { useCandleWindow, useResearchNoteMutations, useResearchNotes } from './api';

/**
 * Research notes: studies the assistant ran on the chart history, written
 * up for the owner, each with a comment box the assistant reads back.
 */
export function ResearchPage() {
  const { t } = useI18n();
  const notes = useResearchNotes();

  return (
    <div className="space-y-4">
      <header className="flex flex-wrap items-center gap-3">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">{t('fib.title')}</h1>
          <p className="mt-0.5 text-sm text-muted-foreground">{t('fib.researchSubtitle')}</p>
        </div>
        <div className="ms-auto">
          <FibTabs />
        </div>
      </header>

      {notes.error && (
        <div role="alert" className="flex gap-2.5 rounded-lg border border-danger/30 bg-danger/8 p-3 text-sm text-danger">
          <InfoCircle size={18} variant="Bold" className="mt-px shrink-0" />
          <span>{notes.error instanceof Error ? notes.error.message : t('fib.error')}</span>
        </div>
      )}

      {notes.isPending ? (
        <p className="text-sm text-muted-foreground">{t('common.loading')}</p>
      ) : (notes.data?.notes.length ?? 0) === 0 ? (
        <p className="text-sm text-muted-foreground">{t('fib.noResearch')}</p>
      ) : (
        notes.data!.notes.map((n) => <NoteCard key={n.id} note={n} />)
      )}
    </div>
  );
}

function NoteCard({ note }: { note: ResearchNote }) {
  const { t } = useI18n();
  const { theme } = useTheme();
  const palette = chartPalette(theme);
  const mutations = useResearchNoteMutations();
  const [comment, setComment] = useState(note.ownerNote);
  useEffect(() => setComment(note.ownerNote), [note.ownerNote]);

  const hasChart = note.symbol && note.timeframe && note.fromTime !== null && note.toTime !== null;
  const window = useCandleWindow(
    hasChart ? note.symbol : null,
    hasChart ? note.timeframe : null,
    note.fromTime,
    note.toTime,
  );
  const candles = window.data?.candles ?? [];
  const digits = window.data?.digits ?? 2;

  const drawings = useMemo<FibDrawing[]>(
    () =>
      note.drawings.map((d, i) => ({
        tag: d.tag,
        color: i === 0 ? palette.fibTeaching : (palette.fib[note.timeframe ?? 'H4'] ?? palette.text),
        direction: d.direction,
        start: d.start,
        end: d.end,
        levels: fibLevels(d),
        digits,
      })),
    [note.drawings, note.timeframe, palette, digits],
  );

  const date = new Date(note.createdAt * 1000).toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' });

  return (
    <article className="glass space-y-3 rounded-2xl p-4">
      <header>
        <h2 className="text-lg font-semibold">{note.title}</h2>
        <p className="text-xs text-muted-foreground">
          {date}
          {note.timeframe && ` · ${timeframeShort(note.timeframe)}`}
        </p>
      </header>

      <Markdown text={note.body} />

      {hasChart && (
        <ChartFrame label={note.title} fill={false} className="group/frame h-[26rem] overflow-hidden rounded-xl border border-border/60">
          {window.isPending ? (
            <div className="flex h-full items-center justify-center text-sm text-muted-foreground">{t('common.loading')}</div>
          ) : candles.length === 0 ? (
            <div className="flex h-full items-center justify-center text-sm text-muted-foreground">{t('common.none')}</div>
          ) : (
            <FibChart
              className="h-full w-full"
              candles={candles}
              drawings={drawings}
              visibleBars={candles.length}
              digits={digits}
              theme={theme}
              resetKey={`research-${note.id}`}
            />
          )}
        </ChartFrame>
      )}

      <div>
        <label className="mb-1 block text-xs font-medium text-muted-foreground">{t('fib.yourComment')}</label>
        <textarea
          value={comment}
          onChange={(e) => setComment(e.target.value)}
          onBlur={() => {
            if (comment !== note.ownerNote) mutations.comment.mutate({ id: note.id, ownerNote: comment });
          }}
          rows={2}
          placeholder={t('fib.commentPlaceholder')}
          className="w-full rounded-md border border-input bg-background px-2 py-1.5 text-sm outline-none placeholder:text-muted-foreground/60 focus-visible:border-ring"
        />
      </div>
    </article>
  );
}

/**
 * A small markdown: `#`/`##` headings, `-` bullets, `|` tables, blank-line
 * paragraphs, and **bold**. Enough for a study write-up; nothing more.
 */
function Markdown({ text }: { text: string }) {
  const blocks = useMemo(() => text.replace(/\r/g, '').split(/\n{2,}/), [text]);
  return (
    <div className="space-y-2 text-sm leading-relaxed">
      {blocks.map((block, i) => {
        const lines = block.split('\n').filter((l) => l.trim() !== '');
        if (lines.length === 0) return null;
        if (lines.every((l) => l.trim().startsWith('|'))) {
          const rows = lines.map((l) => l.trim().replace(/^\||\|$/g, '').split('|').map((c) => c.trim()));
          const body = rows.filter((r) => !r.every((c) => /^:?-{2,}:?$/.test(c)));
          const [head, ...rest] = body;
          return (
            <div key={i} className="overflow-x-auto">
              <table className="min-w-full text-xs">
                <thead>
                  <tr>{head!.map((c, j) => <th key={j} className="border-b border-border px-2 py-1 text-start font-medium">{inline(c)}</th>)}</tr>
                </thead>
                <tbody>
                  {rest.map((r, k) => (
                    <tr key={k}>{r.map((c, j) => <td key={j} className="tabular border-b border-border/40 px-2 py-1">{inline(c)}</td>)}</tr>
                  ))}
                </tbody>
              </table>
            </div>
          );
        }
        if (lines.every((l) => /^\s*-\s/.test(l))) {
          return (
            <ul key={i} className="list-disc space-y-1 ps-5">
              {lines.map((l, j) => <li key={j}>{inline(l.replace(/^\s*-\s/, ''))}</li>)}
            </ul>
          );
        }
        const first = lines[0]!;
        if (first.startsWith('## ')) return <h3 key={i} className="pt-1 text-base font-semibold">{inline(first.slice(3))}</h3>;
        if (first.startsWith('# ')) return <h3 key={i} className="pt-1 text-base font-semibold">{inline(first.slice(2))}</h3>;
        return <p key={i}>{inline(lines.join(' '))}</p>;
      })}
    </div>
  );
}

function inline(s: string): ReactNode {
  const parts = s.split(/(\*\*[^*]+\*\*)/g);
  return parts.map((p, i) =>
    p.startsWith('**') && p.endsWith('**') ? <strong key={i}>{p.slice(2, -2)}</strong> : <span key={i}>{p}</span>,
  );
}
